"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { dmsApi } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Share2, FolderOpen, File, Download } from "lucide-react";
import type { ShareRecord } from "./share-resource-dialog";

function sharedByLabel(s: ShareRecord): string {
  const u = s.sharedByUserId;
  if (typeof u === "object" && u && "name" in u && u.name) {
    return String(u.name);
  }
  return "—";
}

export interface SharedWithMePageProps {
  onOpenSharedFolder: (
    folderId: string,
    context: { rootFolderId: string; permissionLevel: "view" | "edit" }
  ) => void;
  onOpenSharedFile: (fileId: string, permissionLevel: "view" | "edit") => void;
}

interface ShareCardItem {
  share: ShareRecord;
  resourceName: string;
  resourceMimeType?: string;
  isImagePreview: boolean;
}

export function SharedWithMePage({
  onOpenSharedFolder,
  onOpenSharedFile,
}: SharedWithMePageProps) {
  const [items, setItems] = useState<ShareCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharesApiMissing, setSharesApiMissing] = useState(false);

  const mapIncomingShares = useCallback(async (rows: ShareRecord[]) => {
    const enriched = await Promise.all(
      rows.map(async (share): Promise<ShareCardItem> => {
        if (share.resourceType === "folder") {
          try {
            const folder = await dmsApi.folders.get(share.resourceId);
            return {
              share,
              resourceName: folder?.name ?? "Folder",
              isImagePreview: false,
            };
          } catch {
            return {
              share,
              resourceName: "Folder",
              isImagePreview: false,
            };
          }
        }

        try {
          const file = await dmsApi.files.get(share.resourceId);
          const mimeType = file?.mimeType as string | undefined;
          return {
            share,
            resourceName: file?.originalName ?? file?.name ?? "File",
            resourceMimeType: mimeType,
            isImagePreview: Boolean(mimeType?.startsWith("image/")),
          };
        } catch {
          return {
            share,
            resourceName: "File",
            isImagePreview: false,
          };
        }
      })
    );

    setItems(enriched);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setSharesApiMissing(false);
    try {
      const data = await dmsApi.shares.incoming();
      const rows = Array.isArray(data) ? data : [];
      await mapIncomingShares(rows);
    } catch (err: unknown) {
      const e = err as { error?: string; message?: string; status?: number };
      const msg = (e?.error ?? e?.message ?? "").toString().toLowerCase();
      const notFound =
        e?.status === 404 ||
        msg.includes("not found") ||
        msg === "not found";
      if (notFound) {
        setSharesApiMissing(true);
        setItems([]);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: e?.error ?? e?.message ?? "Failed to load shared items.",
        });
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [mapIncomingShares]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col h-full overflow-auto">
      <Card className="m-6 flex-1 min-h-0 flex flex-col">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Share2 size={24} />
              Shared with me
            </CardTitle>
            <CardDescription>
              Files and folders others shared with you. Folder access applies to everything inside.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}>
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto space-y-4">
          {sharesApiMissing && (
            <Alert variant="destructive">
              <AlertTitle>Shares API not available</AlertTitle>
              <AlertDescription className="text-sm mt-1 space-y-2">
                <p>
                  This server responded with <strong>404</strong> for{" "}
                  <code className="text-xs bg-background/80 px-1 rounded">GET /api/shares/incoming</code>.
                  The deployed DMS API does not include share routes yet (they are not listed in{" "}
                  <code className="text-xs bg-background/80 px-1 rounded">/api-docs</code> on production).
                </p>
                <p>
                  Deploy an API build that registers the Shares module, or point the app at a dev API that
                  has <code className="text-xs bg-background/80 px-1 rounded">/api/shares/incoming</code>{" "}
                  (same JWT as the rest of the app). Next.js rewrites already forward{" "}
                  <code className="text-xs bg-background/80 px-1 rounded">/api/*</code> to your API URL.
                </p>
              </AlertDescription>
            </Alert>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 && !sharesApiMissing ? (
            <p className="text-muted-foreground text-center py-8">Nothing shared with you yet.</p>
          ) : !loading && items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((row) => {
                const s = row.share;
                const isFolder = s.resourceType === "folder";
                return (
                  <Card key={s._id} className="overflow-hidden">
                    <div className="aspect-[4/3] border-b bg-muted/20 flex items-center justify-center">
                      {isFolder ? (
                        <div className="flex flex-col items-center gap-2 text-blue-600">
                          <FolderOpen size={40} />
                          <span className="text-xs uppercase tracking-wide text-muted-foreground">Folder</span>
                        </div>
                      ) : row.isImagePreview ? (
                        <div className="relative h-full w-full">
                          <Image
                            src={dmsApi.files.getFileViewUrl(s.resourceId)}
                            alt={row.resourceName}
                            fill
                            className="object-cover"
                            unoptimized
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <File size={40} />
                          <span className="text-xs uppercase tracking-wide">File</span>
                        </div>
                      )}
                    </div>

                    <CardContent className="p-3 space-y-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium truncate" title={row.resourceName}>
                          {row.resourceName}
                        </p>
                        {!isFolder && row.resourceMimeType ? (
                          <p className="text-[11px] text-muted-foreground truncate">{row.resourceMimeType}</p>
                        ) : null}
                        <p className="text-xs text-muted-foreground">
                          Shared by {sharedByLabel(s)}
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <Badge variant={s.permissionLevel === "edit" ? "default" : "secondary"}>
                          {s.permissionLevel}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground capitalize">{s.resourceType}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        {isFolder ? (
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() =>
                              onOpenSharedFolder(s.resourceId, {
                                rootFolderId: s.resourceId,
                                permissionLevel: s.permissionLevel,
                              })
                            }
                          >
                            Open
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              className="flex-1"
                              onClick={() => onOpenSharedFile(s.resourceId, s.permissionLevel)}
                            >
                              Open
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await dmsApi.files.download(s.resourceId, row.resourceName);
                                  toast({
                                    title: "Download started",
                                    description: `"${row.resourceName}" is downloading.`,
                                  });
                                } catch (err: unknown) {
                                  const e = err as { message?: string };
                                  toast({
                                    variant: "destructive",
                                    title: "Download failed",
                                    description: e?.message ?? "Could not download file.",
                                  });
                                }
                              }}
                            >
                              <Download size={14} className="mr-1" />
                              Download
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
